import {
  AuthLink,
  CreateUserResponse,
  OkResponse,
  type Role,
  SessionList,
  User,
  UserList,
} from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { z } from 'zod';
import { Icon } from '../../components/Icon';
import { Drawer, Modal } from '../../components/Overlays';
import { PageHead } from '../../components/PageHead';
import { Alert, EmptyState, ErrorBox, Skeleton } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api, newIdempotencyKey } from '../../lib/api';
import { copyText, errorMessage, formatWhen } from '../../lib/messages';
import { ROLE_LABELS, initials, useMe } from '../../lib/session';
import { useTheme } from '../../lib/theme';
import { ApiTokensSection } from './ApiTokens';
import { LoginAttemptsSection } from './LoginAttempts';
import { TwoFactorSection } from './TwoFactor';

const usersKey = ['users'] as const;
const COLS = '2.2fr 1fr 1.2fr 1fr 1.8fr';

type Dialog =
  | { kind: 'invite' }
  | { kind: 'link'; title: string; link: AuthLink; name: string; invite: boolean }
  | { kind: 'role'; user: User }
  | { kind: 'reset' | 'deactivate' | 'cancel' | 'reset2fa'; user: User }
  | null;

function statusBadge(u: User) {
  if (u.status === 'invited') return <span className="badge warn">Convite pendente</span>;
  if (u.status === 'disabled') return <span className="badge">Desativado</span>;
  return (
    <span className={u.role === 'admin' ? 'badge solid' : 'badge info'}>{ROLE_LABELS[u.role]}</span>
  );
}

function sessionsLabel(u: User): string {
  if (u.status === 'invited') return '—';
  if (u.activeSessions === 0) return 'nenhuma';
  return `${u.activeSessions} ${u.activeSessions === 1 ? 'aberta' : 'abertas'}`;
}

function RoleSeg({ value, onChange }: { value: Role; onChange: (r: Role) => void }) {
  return (
    <div className="seg" style={{ alignSelf: 'flex-start' }} role="radiogroup" aria-label="Papel">
      {(['admin', 'operator'] as const).map((r) => (
        <button
          key={r}
          type="button"
          role="radio"
          aria-checked={value === r}
          className={value === r ? 'on' : ''}
          onClick={() => onChange(r)}
        >
          {ROLE_LABELS[r]}
        </button>
      ))}
    </div>
  );
}

const ROLE_HINT: Record<Role, string> = {
  admin: 'Admin altera custos, canais, usuários e backups.',
  operator:
    'Operadora vende, produz e vê relatórios; custos, canais, usuários e dados só para ver.',
};

function Can({ admin, op }: { admin: boolean; op: boolean }) {
  return (
    <>
      <span className={admin ? 'yes' : 'no'}>{admin ? '✓' : '✕'}</span>
      <span className={op ? 'yes' : 'no'}>{op ? '✓' : '✕'}</span>
    </>
  );
}

function InviteDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (r: CreateUserResponse) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('operator');
  const [touched, setTouched] = useState(false);
  const [key] = useState(newIdempotencyKey);
  const create = useMutation({
    mutationFn: () =>
      api.post(
        '/users',
        CreateUserResponse,
        { name: name.trim(), email: email.trim(), role },
        { idempotencyKey: key },
      ),
    onSuccess: onDone,
  });
  const emailBad = touched && !z.email().safeParse(email.trim()).success;
  const nameBad = touched && name.trim() === '';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (name.trim() === '' || !z.email().safeParse(email.trim()).success) return;
    create.mutate();
  };

  return (
    <Modal
      title="Convidar usuário"
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" form="invite-form" className="btn p" disabled={create.isPending}>
            Criar convite
          </button>
        </>
      }
    >
      <form id="invite-form" className="col" style={{ gap: 16 }} onSubmit={submit} noValidate>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(min(240px,100%),1fr))',
            gap: 14,
          }}
        >
          <div className="field">
            <label htmlFor="iv-n">Nome</label>
            <input
              id="iv-n"
              autoComplete="off"
              className={nameBad ? 'input err' : 'input'}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Como aparece no sistema"
            />
            {nameBad ? (
              <span className="help err">
                <Icon name="alert" size={14} />
                Digite o nome.
              </span>
            ) : null}
          </div>
          <div className="field">
            <label htmlFor="iv-e">E-mail</label>
            <input
              id="iv-e"
              autoComplete="off"
              type="email"
              className={emailBad ? 'input err' : 'input'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@loja.com.br"
            />
            {emailBad ? (
              <span className="help err">
                <Icon name="alert" size={14} />
                Digite um e-mail válido: é com ele que a pessoa entra.
              </span>
            ) : null}
          </div>
        </div>
        <div className="field">
          <span className="lbl">Papel</span>
          <RoleSeg value={role} onChange={setRole} />
        </div>
        <div className="card sunk" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="gt">
            <div className="gtr head" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
              <span>Pode…</span>
              <span>Admin</span>
              <span>Operadora</span>
            </div>
            {(
              [
                ['Orçamentos, pedidos, fila, estoque, despesas', true, true],
                ['Ver custos, regras e canais', true, true],
                ['Alterar custos, regras e canais', true, false],
                ['Ver relatórios', true, true],
                ['Ver usuários e backups', true, true],
                ['Alterar usuários, backups, importar e lixeira', true, false],
              ] as const
            ).map(([label, a, o]) => (
              <div key={label} className="gtr" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
                <span>{label}</span>
                <Can admin={a} op={o} />
              </div>
            ))}
          </div>
        </div>
        <p className="xs muted">
          O convite vale por 7 dias. Você recebe um link para enviar à pessoa (por WhatsApp, por
          exemplo), e ela cria a própria senha.
        </p>
        {create.isError ? <Alert tone="err">{errorMessage(create.error)}</Alert> : null}
      </form>
    </Modal>
  );
}

function LinkDialog({
  title,
  link,
  name,
  invite,
  onClose,
}: {
  title: string;
  link: AuthLink;
  name: string;
  invite: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(
    invite
      ? `Oi, ${name}! Seu acesso à Deizi3d: abra o link para criar sua senha. ${link.url}`
      : `Oi, ${name}! Link para criar uma senha nova na Deizi3d: ${link.url}`,
  )}`;
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <button type="button" className="btn p" onClick={onClose}>
          Pronto
        </button>
      }
    >
      <p>
        Copie o link e envie para <b>{name}</b>. Ele vale até <b>{formatWhen(link.expiresAt)}</b> e
        só funciona uma vez.
      </p>
      <div className="linkbox">
        <code>{link.url}</code>
        <button
          type="button"
          className="btn sm"
          onClick={() =>
            void copyText(link.url).then((ok) =>
              toast(ok ? 'Link copiado' : 'Não deu para copiar; selecione o link'),
            )
          }
        >
          <Icon name="copy" size={16} />
          Copiar
        </button>
      </div>
      <a className="btn block" href={whatsapp} target="_blank" rel="noreferrer">
        Enviar pelo WhatsApp
      </a>
      <p className="xs muted">
        Por segurança, este link não aparece de novo. Se perder, gere outro.
      </p>
    </Modal>
  );
}

function SessionsDrawer({ user, onClose }: { user: User; onClose: () => void }) {
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const isAdmin = me.role === 'admin';
  const isSelf = me.id === user.id;
  const canEnd = isAdmin || isSelf;
  const key = ['users', user.id, 'sessions'];
  const sessions = useQuery({
    queryKey: key,
    queryFn: () => api.get(`/users/${user.id}/sessions`, SessionList),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: usersKey });
  };
  const end = useMutation({
    mutationFn: (id: string) => api.delete(`/sessions/${id}`, OkResponse),
    onSuccess: () => {
      toast('Sessão encerrada');
      refresh();
    },
  });
  const endOthers = useMutation({
    mutationFn: () =>
      api.post(`/users/${user.id}/sessions/revoke-others`, z.object({ revoked: z.number() })),
    onSuccess: (r) => {
      toast(r.revoked === 1 ? '1 sessão encerrada' : `${r.revoked} sessões encerradas`);
      refresh();
    },
  });
  const list = sessions.data?.data ?? [];
  const others = list.filter((s) => !s.current);

  return (
    <Drawer kicker="Sessões ativas" title={user.name} onClose={onClose}>
      {sessions.isPending ? <Skeleton rows={2} /> : null}
      {sessions.isError ? (
        <ErrorBox
          title="Não foi possível carregar as sessões"
          onRetry={() => void sessions.refetch()}
        />
      ) : null}
      {sessions.isSuccess && list.length === 0 ? (
        <div className="empty" style={{ padding: '24px 8px' }}>
          <div className="emptyart" />
          <h3>Nenhuma sessão aberta</h3>
          <p className="sm">Na próxima entrada, a sessão aparece aqui.</p>
        </div>
      ) : null}
      <div className="col" style={{ gap: 0 }}>
        {list.map((s) => (
          <div key={s.id} className="listrow">
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: 'var(--sunk)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 40px',
              }}
            >
              <Icon name={/iPhone|Android/.test(s.device) ? 'phone' : 'laptop'} />
            </span>
            <div className="grow" style={{ minWidth: 0 }}>
              <p style={{ fontWeight: 500 }}>{s.device}</p>
              <p className="xs muted">
                {s.current ? 'ativa agora' : `último acesso ${formatWhen(s.lastSeenAt)}`}
                {s.ip ? ` · ${s.ip}` : ''}
              </p>
            </div>
            {s.current ? <span className="badge ok">Esta sessão</span> : null}
            {!s.current && canEnd ? (
              <button
                type="button"
                className="btn sm d"
                onClick={() => end.mutate(s.id)}
                disabled={end.isPending}
              >
                Encerrar
              </button>
            ) : null}
          </div>
        ))}
      </div>
      {canEnd && others.length > 0 ? (
        <button
          type="button"
          className="btn d block"
          onClick={() => endOthers.mutate()}
          disabled={endOthers.isPending}
        >
          {isSelf ? 'Encerrar as outras sessões' : 'Encerrar todas'}
        </button>
      ) : null}
      {!canEnd ? (
        <p className="row sm muted" style={{ gap: 6 }}>
          <Icon name="lock" size={14} />
          Só Admin encerra sessões de outras pessoas. As suas você encerra no seu nome.
        </p>
      ) : null}
      {end.isError || endOthers.isError ? (
        <Alert tone="err">{errorMessage(end.error ?? endOthers.error)}</Alert>
      ) : null}
      <p className="xs muted">
        Encerrar pede senha de novo naquele aparelho. Use se um celular foi perdido ou trocado.
      </p>
    </Drawer>
  );
}

function RoleDialog({ user, onClose }: { user: User; onClose: () => void }) {
  const [role, setRole] = useState<Role>(user.role);
  const qc = useQueryClient();
  const toast = useToast();
  const save = useMutation({
    mutationFn: () => api.patch(`/users/${user.id}`, User, { role }),
    onSuccess: () => {
      toast('Papel atualizado');
      void qc.invalidateQueries({ queryKey: usersKey });
      onClose();
    },
  });
  return (
    <Modal
      title={`Alterar papel de ${user.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            onClick={() => save.mutate()}
            disabled={save.isPending || role === user.role}
          >
            Salvar papel
          </button>
        </>
      }
    >
      <RoleSeg value={role} onChange={setRole} />
      <p className="sm muted">{ROLE_HINT[role]}</p>
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
    </Modal>
  );
}

function ConfirmDialog({
  dialog,
  onClose,
  onLink,
}: {
  dialog: { kind: 'reset' | 'deactivate' | 'cancel' | 'reset2fa'; user: User };
  onClose: () => void;
  onLink: (link: AuthLink) => void;
}) {
  const { user } = dialog;
  const qc = useQueryClient();
  const toast = useToast();
  const action = useMutation({
    mutationFn: async () => {
      if (dialog.kind === 'reset') return api.post(`/users/${user.id}/password-reset`, AuthLink);
      if (dialog.kind === 'deactivate') return api.post(`/users/${user.id}/deactivate`, User);
      if (dialog.kind === 'reset2fa') {
        return api.post(`/users/${user.id}/two-factor/reset`, OkResponse);
      }
      return api.delete(`/users/${user.id}`, OkResponse);
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: usersKey });
      if (dialog.kind === 'reset') {
        onLink(AuthLink.parse(result));
        return;
      }
      toast(
        {
          deactivate: `${user.name} desativado`,
          cancel: 'Convite cancelado',
          reset2fa: `Verificação em duas etapas de ${user.name} desativada`,
        }[dialog.kind],
      );
      onClose();
    },
  });
  const copy = {
    reset: {
      title: 'Redefinir senha?',
      body: `Você recebe um link para enviar a ${user.name}. A senha atual continua valendo até a nova ser criada.`,
      btn: 'Gerar link',
      cls: 'btn p',
    },
    deactivate: {
      title: `Desativar ${user.name}?`,
      body: 'As sessões abertas são encerradas e a pessoa não entra mais. O histórico de pedidos e orçamentos continua.',
      btn: 'Desativar',
      cls: 'btn d',
    },
    reset2fa: {
      title: `Redefinir a verificação em duas etapas de ${user.name}?`,
      body: `Para quando o celular foi perdido: a verificação é desativada e as sessões de ${user.name} são encerradas. A pessoa entra só com a senha e pode ativar de novo em Configurações · Usuários.`,
      btn: 'Redefinir',
      cls: 'btn d',
    },
    cancel: {
      title: 'Cancelar convite?',
      body: `O link enviado para ${user.email} para de funcionar.`,
      btn: 'Cancelar convite',
      cls: 'btn d',
    },
  }[dialog.kind];

  return (
    <Modal
      title={copy.title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className={copy.cls}
            onClick={() => action.mutate()}
            disabled={action.isPending}
          >
            {copy.btn}
          </button>
        </>
      }
    >
      <p>{copy.body}</p>
      {action.isError ? <Alert tone="err">{errorMessage(action.error)}</Alert> : null}
    </Modal>
  );
}

function UserRow({
  user,
  isAdmin,
  isSelf,
  onDialog,
  onSessions,
  onResend,
  onReactivate,
}: {
  user: User;
  isAdmin: boolean;
  isSelf: boolean;
  onDialog: (d: Dialog) => void;
  onSessions: () => void;
  onResend: () => void;
  onReactivate: () => void;
}) {
  const { dark } = useTheme();
  const [menu, setMenu] = useState(false);
  const pending = user.status === 'invited';
  const active = user.status === 'active';
  const off = user.status === 'disabled';
  const canSeeSessions = !pending && (isAdmin || isSelf);

  return (
    <div className="gtr stack-sm" style={{ gridTemplateColumns: COLS, position: 'relative' }}>
      <div className="row" style={{ gap: 10, minWidth: 0 }}>
        <span className={dark ? 'avatar co' : 'avatar'}>{initials(user.name)}</span>
        <div style={{ minWidth: 0 }}>
          <p style={{ fontWeight: 500 }} className="trunc" title={user.name}>
            {user.name}
            {isSelf ? <span className="xs muted"> (você)</span> : null}
          </p>
          <p className="xs muted trunc" title={user.email}>
            {user.email}
          </p>
        </div>
      </div>
      <span className="row wrap" style={{ gap: 4 }}>
        {statusBadge(user)}
        {user.twoFactorEnabled ? (
          <span className="badge ok" title="Verificação em duas etapas ativada">
            2FA
          </span>
        ) : null}
      </span>
      <span className="sm">{sessionsLabel(user)}</span>
      <span className="sm tab">{pending ? 'convite enviado' : formatWhen(user.lastSeenAt)}</span>
      <div className="row wrap" style={{ gap: 4, justifyContent: 'flex-end' }}>
        {isAdmin && pending ? (
          <button type="button" className="btn sm g" onClick={onResend}>
            Novo link de convite
          </button>
        ) : null}
        {isAdmin && active ? (
          <button
            type="button"
            className="btn sm g"
            onClick={() => onDialog({ kind: 'reset', user })}
          >
            Redefinir senha
          </button>
        ) : null}
        {canSeeSessions ? (
          <button type="button" className="btn sm g" onClick={onSessions}>
            Sessões
          </button>
        ) : null}
        {isAdmin ? (
          <button
            type="button"
            className="btn sm icon g"
            aria-label={`Mais ações para ${user.name}`}
            aria-expanded={menu}
            onClick={() => setMenu((m) => !m)}
          >
            <Icon name="dots" />
          </button>
        ) : null}
      </div>
      {menu ? (
        <>
          <button
            type="button"
            aria-label="Fechar menu"
            onClick={() => setMenu(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 15,
              background: 'transparent',
              border: 0,
              cursor: 'default',
            }}
          />
          <div
            className="menu"
            role="menu"
            style={{ position: 'absolute', right: 16, top: 54, zIndex: 20 }}
          >
            {pending ? (
              <button
                type="button"
                role="menuitem"
                style={{ color: 'var(--err)' }}
                onClick={() => onDialog({ kind: 'cancel', user })}
              >
                Cancelar convite
              </button>
            ) : null}
            {active ? (
              <>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => onDialog({ kind: 'role', user })}
                >
                  Alterar papel
                </button>
                {user.twoFactorEnabled ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => onDialog({ kind: 'reset2fa', user })}
                  >
                    Redefinir verificação em duas etapas
                  </button>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  style={{ color: 'var(--err)' }}
                  onClick={() => onDialog({ kind: 'deactivate', user })}
                >
                  Desativar
                </button>
              </>
            ) : null}
            {off ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenu(false);
                  onReactivate();
                }}
              >
                Reativar
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

/** design/Config-Usuarios.dc.html + API tokens (no board; same patterns). */
export function UsersPage() {
  const me = useMe();
  const isAdmin = me.role === 'admin';
  const qc = useQueryClient();
  const toast = useToast();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [sessionsFor, setSessionsFor] = useState<User | null>(null);
  const users = useQuery({ queryKey: usersKey, queryFn: () => api.get('/users', UserList) });

  const resend = useMutation({
    mutationFn: (u: User) =>
      api.post(`/users/${u.id}/invite`, AuthLink).then((link) => ({ link, u })),
    onSuccess: ({ link, u }) =>
      setDialog({ kind: 'link', title: 'Novo link de convite', link, name: u.name, invite: true }),
    onError: (err) => toast(errorMessage(err)),
  });
  const reactivate = useMutation({
    mutationFn: (u: User) => api.post(`/users/${u.id}/reactivate`, User),
    onSuccess: (u) => {
      toast(`${u.name} reativado`);
      void qc.invalidateQueries({ queryKey: usersKey });
    },
    onError: (err) => toast(errorMessage(err)),
  });

  const list = users.data?.data ?? [];
  const activeCount = list.filter((u) => u.status === 'active').length;
  const pendingCount = list.filter((u) => u.status === 'invited').length;
  const activeAdmins = list.filter((u) => u.status === 'active' && u.role === 'admin').length;
  const close = () => setDialog(null);

  return (
    <>
      <PageHead
        crumb="Configurações"
        title="Usuários e acessos"
        actions={
          isAdmin ? (
            <button type="button" className="btn p" onClick={() => setDialog({ kind: 'invite' })}>
              <Icon name="plus" size={18} strokeWidth={2.4} />
              Convidar usuário
            </button>
          ) : (
            <div className="row" style={{ gap: 8 }}>
              <span className="badge info">Vendo como Operadora</span>
              <span className="badge">
                <Icon name="lock" size={14} />
                Só Admin altera
              </span>
            </div>
          )
        }
      />
      {!isAdmin ? (
        <Alert tone="warn">
          <b>Só Admin altera.</b> Você entrou como Operadora: pode ver quem tem acesso e as sessões
          abertas, e encerrar as suas. Convidar, alterar papel, desativar e redefinir senha é com o
          Admin.
        </Alert>
      ) : null}

      <section className="card flat" aria-busy={users.isPending} style={{ overflow: 'visible' }}>
        <div className="row between wrap" style={{ padding: '16px 18px 6px' }}>
          <h2>Usuários</h2>
          {users.isSuccess ? (
            <span className="sm muted">
              {activeCount} {activeCount === 1 ? 'ativo' : 'ativos'} · {pendingCount}{' '}
              {pendingCount === 1 ? 'convite pendente' : 'convites pendentes'}
            </span>
          ) : null}
        </div>
        {users.isPending ? <Skeleton /> : null}
        {users.isError ? (
          <ErrorBox
            title="Não foi possível carregar os usuários"
            onRetry={() => void users.refetch()}
          />
        ) : null}
        {users.isSuccess && list.length > 0 ? (
          <>
            <div className="gt">
              <div className="gtr head stack-sm" style={{ gridTemplateColumns: COLS }}>
                <span>Nome</span>
                <span>Papel</span>
                <span>Sessões ativas</span>
                <span>Último acesso</span>
                <span />
              </div>
              {list.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  isAdmin={isAdmin}
                  isSelf={u.id === me.id}
                  onDialog={setDialog}
                  onSessions={() => setSessionsFor(u)}
                  onResend={() => resend.mutate(u)}
                  onReactivate={() => reactivate.mutate(u)}
                />
              ))}
            </div>
            <p className="xs muted" style={{ padding: '10px 18px 16px' }}>
              {!isAdmin
                ? 'Você vê todos os usuários e as sessões abertas. Só o Admin convida, altera papel ou desativa.'
                : activeAdmins === 1
                  ? 'Você é o único Admin. Para trocar de papel ou se desativar, promova outra pessoa a Admin primeiro.'
                  : 'Admins podem alterar o papel uns dos outros; o sistema sempre mantém pelo menos um Admin ativo.'}
            </p>
          </>
        ) : null}
        {users.isSuccess && list.length === 1 ? (
          <EmptyState
            title="Só você por aqui ainda"
            action={
              isAdmin ? (
                <button
                  type="button"
                  className="btn p"
                  onClick={() => setDialog({ kind: 'invite' })}
                >
                  Convidar usuário
                </button>
              ) : null
            }
          >
            Convide a operadora para lançar orçamentos, pedidos e a fila pelo celular.
          </EmptyState>
        ) : null}
      </section>

      <TwoFactorSection />

      {isAdmin ? <ApiTokensSection /> : null}

      {isAdmin ? <LoginAttemptsSection /> : null}

      <section className="card flat">
        <div className="row between wrap" style={{ padding: '16px 18px 6px', gap: 8 }}>
          <h2>O que cada papel pode fazer</h2>
          <span className="sm muted">
            Edição de custos, canais, usuários e dados é só do Admin.
          </span>
        </div>
        <div className="tblbox">
          <table className="tbl">
            <thead>
              <tr>
                <th>Área</th>
                <th style={{ width: 150 }}>Admin</th>
                <th style={{ width: 220 }}>Operadora</th>
              </tr>
            </thead>
            <tbody>
              {(
                [
                  ['Orçamento rápido e orçamentos', '✓ Tudo', '✓ Criar, enviar, converter', 'yes'],
                  ['Pedidos e clientes', '✓ Tudo', '✓ Criar e atualizar a situação', 'yes'],
                  [
                    'Fila de impressão e impressoras',
                    '✓ Tudo',
                    '✓ Iniciar, concluir, registrar falha',
                    'yes',
                  ],
                  [
                    'Estoque: filamentos, carretéis, insumos',
                    '✓ Tudo',
                    '✓ Lançar compras e pesagens',
                    'yes',
                  ],
                  ['Despesas', '✓ Tudo', '✓ Lançar com recibo', 'yes'],
                  ['Parâmetros de custo, regras de preço e canais', '✓ Editar', 'Só ver', 'part'],
                  ['Relatórios e resultado do mês', '✓ Ver', '✓ Ver', 'yes'],
                  ['Usuários, convites e tokens de API', '✓ Gerenciar', 'Só ver usuários', 'part'],
                  ['Backups, importar, exportar e lixeira', '✓ Tudo', 'Só ver', 'part'],
                ] as const
              ).map(([area, admin, op, cls]) => (
                <tr key={area}>
                  <td>{area}</td>
                  <td className="yes">{admin}</td>
                  <td className={cls}>{op}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {dialog?.kind === 'invite' ? (
        <InviteDialog
          onClose={close}
          onDone={(r) => {
            void qc.invalidateQueries({ queryKey: usersKey });
            setDialog({
              kind: 'link',
              title: 'Convite criado',
              link: r.invite,
              name: r.user.name,
              invite: true,
            });
          }}
        />
      ) : null}
      {dialog?.kind === 'link' ? (
        <LinkDialog
          title={dialog.title}
          link={dialog.link}
          name={dialog.name}
          invite={dialog.invite}
          onClose={close}
        />
      ) : null}
      {dialog?.kind === 'role' ? <RoleDialog user={dialog.user} onClose={close} /> : null}
      {dialog?.kind === 'reset' ||
      dialog?.kind === 'deactivate' ||
      dialog?.kind === 'cancel' ||
      dialog?.kind === 'reset2fa' ? (
        <ConfirmDialog
          dialog={dialog}
          onClose={close}
          onLink={(link) =>
            setDialog({
              kind: 'link',
              title: 'Link para nova senha',
              link,
              name: dialog.user.name,
              invite: false,
            })
          }
        />
      ) : null}
      {sessionsFor ? (
        <SessionsDrawer user={sessionsFor} onClose={() => setSessionsFor(null)} />
      ) : null}
    </>
  );
}
